import React, { useEffect, useMemo, useRef } from "react";

import Modal from "../common/Modal";

const TITLE_ID = "benefit-validation-alert-title";
const CONTACT_LABELS = [
  "WhatsApp",
  "Centro de contacto",
  "Correo electrónico",
];

// Canales de contacto que mostramos como footer fijo. Para actualizar números o
// agregar un canal nuevo, editar SOLO esta constante: el modal lo renderiza
// automáticamente en todas las alertas de validación.
const CONTACT_INTRO =
  "En caso de requerir información adicional, no dude en contactarnos a través de nuestros canales digitales:";
const CONTACT_ITEMS: Array<{ label: string; value: string }> = [
  { label: "WhatsApp", value: "809-658-3888" },
  { label: "Centro de contacto", value: "809-688-4040" },
  { label: "Correo electrónico", value: "contacto@afpcrecer.com.do" },
];

// Frases introductorias que el backend solía agregar al final de los mensajes
// antes de la lista de contactos. Se mantienen aquí solo para que mensajes
// "legacy" (BE no redeployado todavía) no muestren ese texto duplicado con el
// footer fijo. Una vez que el BE limpio esté en producción este saneo es no-op.
const LEGACY_INTRO_PHRASES = [
  "Por favor, dirigirse a su oficina más cercana o contactarnos a través de nuestros canales de atención:",
  "En caso de requerir información adicional, no dudes en contactarnos a través de nuestros canales de atención",
];

interface BenefitValidationAlertProps {
  show: boolean;
  messages: string[];
  onDismiss: () => void;
  title?: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Extrae solo la porción de texto "útil" de un mensaje del backend: descarta
 * cualquier dato de contacto incrustado (WhatsApp:/Centro de contacto:/Correo
 * electrónico:) y la frase introductoria legacy ("Por favor, dirigirse a su
 * oficina..."). El footer fijo del modal se encarga de mostrar esa información.
 */
function extractCleanText(message: string): string {
  const labelGroup = CONTACT_LABELS.map(escapeRegExp).join("|");
  // Encuentra la primera ocurrencia de una etiqueta de contacto y trunca todo
  // lo que venga desde ahí (label + valor + cualquier label que siga, con o sin
  // " / " entre medio). Soporta separadores tipo "WhatsApp: x / Centro: y" y
  // también el formato sin slash de INABIMA.
  const labelPattern = new RegExp(`\\s*(?:${labelGroup}):`, "i");
  const labelMatch = message.match(labelPattern);
  let text = labelMatch ? message.substring(0, labelMatch.index ?? 0) : message;

  for (const phrase of LEGACY_INTRO_PHRASES) {
    const trailingPattern = new RegExp(
      `\\s*${escapeRegExp(phrase)}\\s*$`,
      "i"
    );
    text = text.replace(trailingPattern, "");
  }

  return text.trim();
}

export default function BenefitValidationAlert({
  show,
  messages,
  onDismiss,
  title = "Aviso importante",
}: BenefitValidationAlertProps) {
  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (show) {
      dismissButtonRef.current?.focus();
    }
  }, [show]);

  const textParagraphs = useMemo(
    () =>
      messages
        .map(extractCleanText)
        .filter((text) => text.length > 0),
    [messages]
  );

  return (
    <Modal
      show={show}
      onClose={onDismiss}
      title={title}
      titleId={TITLE_ID}
      size="small"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="space-y-4"
      >
        {textParagraphs.map((text, idx) => (
          <p
            key={idx}
            className="text-body-1 text-gray-70 font-body font-normal"
          >
            {text}
          </p>
        ))}
        <div>
          {/* `mb-2` y la ausencia de `space-y` deliberados: el tailwind config del
              proyecto solo escanea HTMLs así que las utilidades arbitrarias y las
              variantes intermedias de `space-y` no están en el CSS compilado. Las
              líneas de contactos quedan tight por line-height natural (24px). */}
          <p className="text-body-1 text-gray-70 font-body font-normal mb-2">
            {CONTACT_INTRO}
          </p>
          {CONTACT_ITEMS.map((item) => (
            <p
              key={item.label}
              className="text-body-1 text-gray-70 font-body font-normal"
            >
              <span className="font-semibold">{item.label}:</span>{" "}
              {item.value}
            </p>
          ))}
        </div>
        <div className="pt-2">
          <button
            ref={dismissButtonRef}
            type="button"
            className="btn btn-primary btn-large w-full"
            onClick={onDismiss}
          >
            Entendido
          </button>
        </div>
      </div>
    </Modal>
  );
}
